import { useRef, type KeyboardEvent, type ReactElement } from "react";
import { ONLINE_QUERY_MAX_CHARS, type LookupHit, type OnlinePlayer } from "odyssey-shared";
import { Button, EmptyState, Glyph, KIT_METRICS, TextField, WholeText, listRowHeight, logical, useInteraction, type GlyphName } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import type { LookupSnapshot } from "./lookup.ts";
import type { OnlineSnapshot } from "./online.ts";

/**
 * The Online players column (ADR-056's amendment of 2026-10-06, decisions 1, 3, 6 and 7; the design's
 * `moderation-window` card, a visual reference): one search field, then everyone online grouped by map with the
 * GM's own map first, each group under its header and count, each row a player's first name and family with their
 * marks; under them, from the same search, the families and characters not in the world under "Not online" (ADR-056's
 * note of 2026-10-08 on one search box: "yeah prefer one box, the one on the left is the search operator, the right
 * is where the action happens"); then the key to the marks. In the narrow window it is the Online tab.
 *
 * Keys (decision 6): Tab reaches the search field, then the list as one stop. In the list Up and Down move the row
 * and Home and End go to the first and the last, moving focus only; Enter, a click or a tap selects the row, which
 * writes its family into the form and reads that family; Enter also moves on to the Player tab. Nothing here sends
 * an action. Every mark carries its word as its name, and nothing is shown on hover alone.
 */

/**
 * The marks a row can carry, each with its glyph, its word and its colour's class.
 */
const MARKS: readonly { readonly key: "you" | "staff" | "frozen" | "muted"; readonly glyph: GlyphName }[] = [
  { key: "you", glyph: "user-small" },
  { key: "staff", glyph: "shield" },
  { key: "frozen", glyph: "hourglass" },
  { key: "muted", glyph: "chat-off" },
];

/**
 * A player's marks, in the key's order.
 *
 * @param player The player.
 * @returns The marks it carries.
 */
function marksOf(player: OnlinePlayer): readonly (typeof MARKS)[number][] {
  return MARKS.filter((mark) => player[mark.key]);
}

/**
 * One mark: its glyph in its colour, named by its word.
 *
 * @param props The mark.
 * @returns The mark.
 */
function Mark({ mark }: { readonly mark: (typeof MARKS)[number] }): ReactElement {
  return (
    <span className={`ot-moderation__mark ot-moderation__mark--${mark.key}`} data-mark={mark.key}>
      <Glyph name={mark.glyph} label={say(`moderation.online.marks.${mark.key}`)} />
    </span>
  );
}

/**
 * A list row's key for an online player.
 *
 * @param player The player.
 * @returns The key.
 */
function onlineKey(player: OnlinePlayer): string {
  return `on:${player.family.toLowerCase()}`;
}

/**
 * A list row's key for a hit not online.
 *
 * @param hit The hit.
 * @returns The key.
 */
function hitKey(hit: LookupHit): string {
  return `off:${hit.kind}:${hit.family.toLowerCase()}:${hit.name.toLowerCase()}`;
}

/**
 * A section's heading in the list: its label in brass and its count at the right, muted, hidden from assistive
 * technology, which reads the section's `group` by the same label.
 *
 * @param props The `label` and the `count`.
 * @returns The heading.
 */
function ListHeading({ label, count }: { readonly label: string; readonly count: number }): ReactElement {
  return (
    <div className="ot-list__heading" aria-hidden="true" style={{ height: logical(KIT_METRICS.list.heading), paddingTop: logical(KIT_METRICS.list.headingTop), paddingLeft: logical(KIT_METRICS.list.paddingX + KIT_METRICS.list.edge), paddingRight: logical(KIT_METRICS.list.paddingX + KIT_METRICS.list.edge) }}>
      <span>{label}</span>
      <span className="ot-list__count">{count}</span>
    </div>
  );
}

/**
 * One player's row: an option holding the first name, the family muted, and the marks at the right.
 *
 * @param props The `player`; whether it is `selected` and the list's `tabStop`; a ref setter; and `onSelect`, run by a
 *   click, a tap, Space and Enter, told whether the key was Enter.
 * @returns The row.
 */
function PlayerRow({ player, selected, tabStop, refer, onSelect }: { readonly player: OnlinePlayer; readonly selected: boolean; readonly tabStop: boolean; readonly refer: (element: HTMLDivElement | null) => void; readonly onSelect: (enter: boolean) => void }): ReactElement {
  const { state, handlers } = useInteraction({ disabled: false, focus: "visible" });
  const metrics = KIT_METRICS.list;
  const marks = marksOf(player);

  /**
   * Space and Enter select the row; neither goes further than the row.
   *
   * @param event The key press.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== " " && event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (!event.repeat) {
      onSelect(event.key === "Enter");
    }
  };

  return (
    <div
      ref={refer}
      role="option"
      aria-selected={selected}
      tabIndex={tabStop ? 0 : -1}
      className={["ot-row", "ot-moderation__player", state.hover && "is-hover", state.pressed && "is-pressed", state.focus && "is-focus", selected && "is-selected"].filter(Boolean).join(" ")}
      data-moderation="player"
      data-family={player.family}
      style={{ height: logical(listRowHeight({ glyph: false, sub: false, trailing: false })), borderWidth: logical(metrics.edge), paddingLeft: logical(metrics.paddingX), paddingRight: logical(metrics.paddingX), paddingTop: logical(metrics.paddingY), gap: logical(metrics.glyphGap) }}
      {...handlers}
      onClick={() => onSelect(false)}
      onKeyDown={onKeyDown}
    >
      <span className="ot-moderation__names">
        <span className="ot-moderation__first">{player.first}</span>
        <span className="ot-moderation__family">{player.family}</span>
      </span>
      {marks.length === 0 ? null : (
        <span className="ot-moderation__marks">
          {marks.map((mark) => (
            <Mark key={mark.key} mark={mark} />
          ))}
        </span>
      )}
    </div>
  );
}

/**
 * One family or character not in the world: an option holding its name and what it is, muted.
 *
 * @param props The `hit`; whether its family is `selected` and the row is the list's `tabStop`; a ref setter; and
 *   `onSelect`, run by a click, a tap, Space and Enter, told whether the key was Enter.
 * @returns The row.
 */
function HitRow({ hit, selected, tabStop, refer, onSelect }: { readonly hit: LookupHit; readonly selected: boolean; readonly tabStop: boolean; readonly refer: (element: HTMLDivElement | null) => void; readonly onSelect: (enter: boolean) => void }): ReactElement {
  const { state, handlers } = useInteraction({ disabled: false, focus: "visible" });
  const metrics = KIT_METRICS.list;

  /**
   * Space and Enter select the row; neither goes further than the row.
   *
   * @param event The key press.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== " " && event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (!event.repeat) {
      onSelect(event.key === "Enter");
    }
  };

  return (
    <div
      ref={refer}
      role="option"
      aria-selected={selected}
      tabIndex={tabStop ? 0 : -1}
      className={["ot-row", "ot-moderation__player", state.hover && "is-hover", state.pressed && "is-pressed", state.focus && "is-focus", selected && "is-selected"].filter(Boolean).join(" ")}
      data-moderation="hit"
      data-family={hit.family}
      style={{ height: logical(listRowHeight({ glyph: false, sub: false, trailing: false })), borderWidth: logical(metrics.edge), paddingLeft: logical(metrics.paddingX), paddingRight: logical(metrics.paddingX), paddingTop: logical(metrics.paddingY), gap: logical(metrics.glyphGap) }}
      {...handlers}
      onClick={() => onSelect(false)}
      onKeyDown={onKeyDown}
    >
      <span className="ot-moderation__names">
        <span className="ot-moderation__first">{hit.name}</span>
        <span className="ot-moderation__family">{hit.kind === "family" ? say("moderation.window.hitFamily") : say("moderation.window.hitCharacter", { family: hit.family })}</span>
      </span>
    </div>
  );
}

/**
 * The column.
 *
 * @param props The model's `snapshot`; the `lookup` of who is not online, run from the same text; `onType`, told the
 *   search field's text; `onSelect`, told the family selected and whether Enter selected it; and `onMore`, Show more.
 * @returns The column.
 */
export function OnlineColumn({ snapshot, lookup, onType, onSelect, onMore }: { readonly snapshot: OnlineSnapshot; readonly lookup: LookupSnapshot; readonly onType: (text: string) => void; readonly onSelect: (family: string, enter: boolean) => void; readonly onMore: () => void }): ReactElement {
  const rows = useRef(new Map<string, HTMLDivElement>());
  const players = snapshot.groups.flatMap((group) => group.players);
  const hits = lookup.answer?.hits ?? [];
  const selected = snapshot.selected?.toLowerCase() ?? null;
  const keys = [...players.map((player) => onlineKey(player)), ...hits.map((hit) => hitKey(hit))];
  const families = [...players.map((player) => player.family), ...hits.map((hit) => hit.family)];
  const chosen = families.findIndex((family) => family.toLowerCase() === selected);
  const tabStop = keys[chosen >= 0 ? chosen : 0];
  const lookupLine = lookup.state === "rate" || lookup.state === "session" || lookup.state === "forbidden" || lookup.state === "failed" ? say(`moderation.online.${lookup.state}`) : lookup.state === "ok" && hits.length === 0 ? say("moderation.online.notOnlineNone") : null;
  const showLookup = lookup.state !== "idle" && (hits.length > 0 || lookupLine !== null);

  /**
   * Moves focus with Up and Down, Home and End, over the online rows and then the rows not online; the selection stays
   * until a row is chosen.
   *
   * @param event The key press inside the list.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const at = keys.findIndex((key) => rows.current.get(key) === event.target);
    if (at < 0) {
      return;
    }
    const to = event.key === "ArrowDown" ? Math.min(keys.length - 1, at + 1) : event.key === "ArrowUp" ? Math.max(0, at - 1) : event.key === "Home" ? 0 : event.key === "End" ? keys.length - 1 : null;
    if (to === null) {
      return;
    }
    event.preventDefault();
    rows.current.get(keys[to] as string)?.focus();
  };

  /**
   * Keeps a row's element by its key, for the keys above.
   *
   * @param key The row's key.
   * @returns The ref setter.
   */
  const refer =
    (key: string) =>
    (element: HTMLDivElement | null): void => {
      if (element === null) {
        rows.current.delete(key);
      } else {
        rows.current.set(key, element);
      }
    };

  return (
    <div className="ot-moderation__online" data-moderation="online">
      <TextField label={say("moderation.online.search")} value={snapshot.text} onChange={(text) => onType(text.slice(0, ONLINE_QUERY_MAX_CHARS))} maxLength={ONLINE_QUERY_MAX_CHARS} />
      {snapshot.line === null ? null : (
        <p className="ot-moderation__line ot-moderation__muted" data-moderation="online-line" role={snapshot.line === say("moderation.online.reading") ? "status" : undefined}>
          {snapshot.line}
        </p>
      )}
      <div className="ot-list ot-moderation__list" data-kit="list">
        {players.length === 0 && !showLookup ? (
          snapshot.line === say("moderation.online.noMatch") ? (
            <EmptyState glyph="user" title={say("moderation.online.noMatch")} />
          ) : null
        ) : (
          <div role="listbox" aria-label={say("moderation.online.heading")} className="ot-list__box ot-scroll" data-kit="listbox" onKeyDown={onKeyDown}>
            {snapshot.groups.map((group) => (
              <div key={group.id} role="group" aria-label={group.header} className="ot-list__section" data-kit="list-section" data-moderation="group">
                <ListHeading label={group.header} count={group.count} />
                {group.players.map((player) => (
                  <PlayerRow key={onlineKey(player)} player={player} selected={player.family.toLowerCase() === selected} tabStop={onlineKey(player) === tabStop} refer={refer(onlineKey(player))} onSelect={(enter) => onSelect(player.family, enter)} />
                ))}
              </div>
            ))}
            {snapshot.more ? (
              <div className="ot-moderation__more">
                <Button onClick={onMore}>{say("moderation.window.more")}</Button>
              </div>
            ) : null}
            {snapshot.capped ? <p className="ot-moderation__line ot-moderation__muted">{say("moderation.online.capped")}</p> : null}
            {showLookup ? (
              <div role="group" aria-label={say("moderation.online.notOnline")} className="ot-list__section" data-kit="list-section" data-moderation="not-online">
                <ListHeading label={say("moderation.online.notOnline")} count={lookup.answer?.total ?? 0} />
                {hits.map((hit) => (
                  <HitRow key={hitKey(hit)} hit={hit} selected={hit.family.toLowerCase() === selected} tabStop={hitKey(hit) === tabStop} refer={refer(hitKey(hit))} onSelect={(enter) => onSelect(hit.family, enter)} />
                ))}
                {lookupLine === null ? null : (
                  <p className={lookup.state === "ok" ? "ot-moderation__line ot-moderation__muted" : "ot-moderation__line ot-moderation__danger"} role="status" data-moderation="lookup-line">
                    {lookupLine}
                  </p>
                )}
                {lookup.answer !== null && lookup.answer.total > hits.length ? <p className="ot-moderation__line ot-moderation__muted" data-moderation="lookup-capped">{say("moderation.window.lookupCapped", { shown: String(hits.length), total: String(lookup.answer.total) })}</p> : null}
              </div>
            ) : null}
          </div>
        )}
      </div>
      <div className="ot-moderation__legend" aria-label={say("moderation.window.legend")} role="list">
        {MARKS.map((mark) => (
          <span key={mark.key} role="listitem" className="ot-moderation__key">
            <span className={`ot-moderation__mark ot-moderation__mark--${mark.key}`}>
              <Glyph name={mark.glyph} />
            </span>
            <WholeText>{say(`moderation.online.marks.${mark.key}`)}</WholeText>
          </span>
        ))}
      </div>
    </div>
  );
}
